import React, { useState, useEffect, useCallback } from 'react';
import {
  Form,
  Input,
  Button,
  Modal,
  Select,
  Upload,
  message,
  InputNumber,
  Row,
  Col,
  List
} from 'antd';
import { UploadOutlined, FileOutlined, DeleteOutlined } from '@ant-design/icons';
import PhoneInput from '../common/PhoneInput';
import ConfirmModal from '../ConfirmModal';
import { useDispatch, useSelector } from 'react-redux';
import { addCandidateFile, deleteCandidateFile } from '../../redux/candidatesSlice';

const { Option } = Select;

const EditModal = ({ visible, onCancel, onSubmit, vacancies, candidate, updateInfo }) => {
  const dispatch = useDispatch();
  const { orgId } = useSelector((state) => state.auth);

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [initialValues, setInitialValues] = useState({});
  const [filesToDelete, setFilesToDelete] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);

  const formatPhoneNumber = (phone) => {

    if (!phone) return '';

    let digits = phone.replace(/\D/g, '');

    if (digits.length > 12) {
      digits = digits.slice(0, 12);
    }

    if (digits.startsWith('375') && digits.length === 12) {
      return `+${digits}`;
    }

    if (digits.startsWith('8') && digits.length === 11) {
      return `+375${digits.slice(1)}`;
    }

    if (digits.length === 9) {
      return `+375${digits}`;
    }

    return phone;
  };

  useEffect(() => {
    if (visible && candidate) {
      let salaryFrom = '';
      let salaryTo = '';
      let salaryCurrency = 'BYN';

      if (candidate.salary_range) {
        const salaryMatch = candidate.salary_range.match(/(\d+)-(\d+)\s*(\w+)/);
        if (salaryMatch) {
          salaryFrom = salaryMatch[1];
          salaryTo = salaryMatch[2];
          salaryCurrency = salaryMatch[3];
        }
      }

      const formValues = {
        full_name: candidate.full_name,
        email: candidate.email,
        phone_number: formatPhoneNumber(candidate.phone_number || ''),
        salary_from: salaryFrom,
        salary_to: salaryTo,
        salary_currency: salaryCurrency,
      };

      setInitialValues(formValues);

      form.setFieldsValue(formValues);

      if (candidate.files && Array.isArray(candidate.files) && candidate.files.length > 0) {
        setExistingFiles(candidate.files);
      } else {
        setExistingFiles([]);
      }

      setFileList([]);
      setFilesToDelete([]);
    }
  }, [visible, candidate, form]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      const changedFields = {};

      Object.keys(values).forEach(key => {
        if (values[key] !== initialValues[key]) {
          changedFields[key] = values[key];
        }
      });

      if (changedFields.salary_from || changedFields.salary_to || changedFields.salary_currency) {
        const salaryRange = `${values.salary_from}-${values.salary_to} ${values.salary_currency}`;
        changedFields.salary_range = salaryRange;

        delete changedFields.salary_from;
        delete changedFields.salary_to;
        delete changedFields.salary_currency;
      }

      // Проверяем, есть ли вообще какие-либо изменения
      const hasChanges = Object.keys(changedFields).length > 0 || fileList.length > 0 || filesToDelete.length > 0;

      if (!hasChanges) {
        message.info('Нет изменений для сохранения');
        onCancel();
        return;
      }

      if (Object.keys(changedFields).length > 0) {
        await onSubmit(changedFields);
      }

      // Удаляем файлы, помеченные для удаления
      if (filesToDelete.length > 0) {
        for (const fileId of filesToDelete) {
          try {
            await dispatch(deleteCandidateFile(fileId)).unwrap();
          } catch (deleteError) {
            console.error('Ошибка при удалении файла:', deleteError);
            message.error('Не удалось удалить файл');
          }
        }
      }

      // Загружаем новые файлы
      if (fileList.length > 0) {
        for (const fileItem of fileList) {
          if (fileItem.originFileObj) {
            const formData = new FormData();
            formData.append("file", fileItem.originFileObj);
            try {
              await dispatch(addCandidateFile({
                id: candidate.id,
                organisationId: orgId,
                file: formData
              })).unwrap();
            } catch (fileError) {
              console.error('Ошибка при загрузке файла:', fileError);
              message.error('Не удалось загрузить файл');
            }
          }
        }
      }

      form.resetFields();
      setFileList([]);
      setFilesToDelete([]);

      await updateInfo();

      // Закрываем модальное окно после успешного сохранения
      onCancel();

    } catch (error) {
      console.error('Form validation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const handleDeleteFile = useCallback((fileId) => {
    setFilesToDelete(prev => [...prev, fileId]);
    setExistingFiles(prev => prev.filter(file => file.id !== fileId));

    // Сохраняем текущие значения формы перед обновлением
    const currentFormValues = form.getFieldsValue();

    // Принудительно обновляем форму с сохраненными значениями
    setTimeout(() => {
      form.setFieldsValue(currentFormValues);
    }, 0);
  }, [form]);

  const renderExistingFiles = useCallback(() => {
    if (!existingFiles || existingFiles.length === 0) {
      return <p>Нет прикрепленных файлов</p>;
    }

    return (
      <List
        size="small"
        dataSource={existingFiles}
        renderItem={file => (
          <List.Item
            style={{ padding: '4px 0' }}
            actions={[
              <Button
                key="delete"
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteFile(file.id)}
                size="small"
                title="Удалить файл"
              />
            ]}
          >
            <Button
              type="link"
              icon={<FileOutlined />}
              size="small"
              onClick={() => window.open('https://dev.1ak-group.com/service/static/' + file.path, '_blank')}
              style={{ padding: 0, height: 'auto' }}
            >
              {file.name || 'Файл'}
            </Button>
          </List.Item>
        )}
      />
    );
  }, [existingFiles, handleDeleteFile]);

  const renderCandidateVacancies = () => {
    if (!candidate || !candidate.vacancies || !Array.isArray(candidate.vacancies) || candidate.vacancies.length === 0) {
      return <p>Нет связанных вакансий</p>;
    }

    return (
      <List
        size="small"
        dataSource={candidate.vacancies}
        renderItem={vacancy => (
          <List.Item style={{ padding: '4px 0' }}>
            <span style={{ fontWeight: 'bold' }}>
              {vacancy.job_title || vacancy.position || 'Без названия'}
            </span>
            {vacancy.organisation_name && (
              <span style={{ color: '#666', marginLeft: 8 }}>
                ({vacancy.organisation_name})
              </span>
            )}
          </List.Item>
        )}
      />
    );
  };

  const beforeUpload = (file) => {
    const isAllowedFormat = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ].includes(file.type);

    if (!isAllowedFormat) {
      message.error('Вы можете загрузить только файлы PDF, DOC или DOCX!');
      return Upload.LIST_IGNORE;
    }

    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('Файл должен быть меньше 5MB!');
      return Upload.LIST_IGNORE;
    }

    return false;
  };

  const hasChanges = useCallback(() => {
    const currentValues = form.getFieldsValue();
    const changedFields = {};

    Object.keys(currentValues).forEach(key => {
      if (currentValues[key] !== initialValues[key]) {
        changedFields[key] = currentValues[key];
      }
    });

    return Object.keys(changedFields).length > 0 || fileList.length > 0 || filesToDelete.length > 0;
  }, [form, initialValues, fileList, filesToDelete]);

  const handleCancel = useCallback(() => {
    if (hasChanges()) {
      setShowConfirm(true);
    } else {
      onCancel();
    }
  }, [hasChanges, onCancel]);

  const handleConfirmClose = useCallback((shouldClose) => {
    if (shouldClose) {
      onCancel();
    }
    setShowConfirm(false);
  }, [onCancel]);

  const footerButtons = [
    <Button key="cancel" onClick={handleCancel}>
      Закрыть
    </Button>,
    <Button
      key="submit"
      type="primary"
      loading={loading}
      onClick={handleSubmit}
    >
      Сохранить
    </Button>
  ];

  return (
    <>
      <Modal
        title="Редактировать кандидата"
        open={visible}
        onCancel={handleCancel}
        width={1000}
        footer={footerButtons}
      >
        <Form form={form} layout="vertical">
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Основная информация
            </h3>

            <Form.Item
              name="full_name"
              label="ФИО"
              rules={[{ required: true, message: 'Введите ФИО кандидата' }]}
            >
              <Input placeholder="Иванов Иван Иванович" />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[{ type: 'email', message: 'Некорректный email' }]}
                >
                  <Input placeholder="example@mail.com" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="phone_number"
                  label="Телефон"
                  rules={[
                    { required: true, message: 'Введите телефон кандидата' },
                    {
                      pattern: /^(\+375\d{9}|\+7\d{10})$/,
                      message: 'Введите корректный номер телефона'
                    }
                  ]}
                >
                  <PhoneInput
                    placeholder="+375 (29) 123-45-67"
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Вакансии и зарплата
            </h3>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 8 }}>
                Связанные вакансии:
              </label>
              {renderCandidateVacancies()}
            </div>

            <Form.Item label="Желаемый уровень дохода">
              <Input.Group compact>
                <Form.Item
                  name="salary_currency"
                  noStyle
                  rules={[{ required: true, message: 'Выберите валюту' }]}
                >
                  <Select placeholder="Валюта" style={{ width: '28%' }}>
                    <Option value="BYN">BYN</Option>
                    <Option value="RUB">RUB</Option>
                    <Option value="USD">USD</Option>
                    <Option value="EUR">EUR</Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  name="salary_from"
                  noStyle
                  rules={[{ required: true, message: 'Введите значение "от"' }]}
                >
                  <InputNumber
                    placeholder="От"
                    style={{ width: '35%', marginLeft: 8 }}
                    min={0}
                  />
                </Form.Item>
                <Form.Item
                  name="salary_to"
                  noStyle
                  rules={[{ required: true, message: 'Введите значение "до"' }]}
                >
                  <InputNumber
                    placeholder="До"
                    style={{ width: '35%', marginLeft: 8 }}
                    min={0}
                  />
                </Form.Item>
              </Input.Group>
            </Form.Item>

            {/* <Form.Item
            name="status"
            label="Статус"
          >
            <Select placeholder="Выберите статус">
              <Option value="new">Новый</Option>
              <Option value="call_back">Перезвонить</Option>
              <Option value="interview">Собеседование</Option>
              <Option value="references">Сбор рекомендаций</Option>
              <Option value="offer">Предложение о работе</Option>
              <Option value="documentation">Оформление документов</Option>
              <Option value="hired">Трудоустройство</Option>
              <Option value="failed">Отклонен</Option>
            </Select>
          </Form.Item> */}
          </div>

          {/* Документы */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Документы
            </h3>

            {/* Существующие файлы */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 8 }}>
                Существующие файлы:
              </label>
              {renderExistingFiles()}
            </div>

            {/* Загрузка новых файлов */}
            <Form.Item
              name="resume_file"
              label="Добавить новые файлы"
              valuePropName="fileList"
              getValueFromEvent={({ fileList }) => fileList}
              help="Допустимые форматы файлов: PDF, DOC, DOCX"
            >
              <Upload
                name="resume"
                fileList={fileList}
                beforeUpload={beforeUpload}
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx"
                maxCount={3}
              >
                <Button icon={<UploadOutlined />}>Загрузить новый файл</Button>
              </Upload>
            </Form.Item>
          </div>
        </Form>
      </Modal>

      <ConfirmModal
        visible={showConfirm}
        onConfirm={handleConfirmClose}
        onCancel={() => setShowConfirm(false)}
        title="Подтверждение закрытия"
        message="Данные не будут сохранены. Продолжить?"
        okText="Продолжить"
        cancelText="Отмена"
        type="warning"
      />
    </>
  );
};

export default EditModal;